import type { TeamMember } from '@qlk/shared';
import { NHAN_THAM_CHIEU, type ThamChieuNguoiDung } from '@qlk/shared';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { HopXacNhan, Nut, O, ngayGio } from '../../../ui/index.js';
import { useDoiKhoaThanhVien, useKhaNangXoa, useLuuTruThanhVien, useXoaThanhVien } from '../api.js';

type Hop = 'khoa' | 'luu-tru' | 'xoa' | null;

interface DongNguyHiemProps {
  ten: string;
  moTa: string;
  nut: ReactNode;
  /** Câu giải thích khi nút bị vô hiệu, hiện ngay dưới dòng. */
  viSaoChan?: string | undefined;
}

/**
 * Một dòng trong "Vùng nguy hiểm": tên + mô tả bên trái, nút bên phải.
 *
 * Nút `shrink-0` còn phần chữ `min-w-0`, nên nút **không bao giờ** rơi xuống
 * dòng dù mô tả dài — chỉ mô tả tự xuống dòng. Màn hẹp hơn `sm` thì xếp dọc
 * hẳn, nút căn lề trái cho thẳng hàng với chữ.
 */
function DongNguyHiem({ ten, moTa, nut, viSaoChan }: DongNguyHiemProps) {
  return (
    <div className="border-t border-bad-chu/15 py-3 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-co-ban font-semibold text-chu">{ten}</p>
          <p className="mt-0.5 text-phu text-chu-phu">{moTa}</p>
        </div>
        <div className="shrink-0">{nut}</div>
      </div>

      {viSaoChan && (
        <p className="mt-2 text-phu text-chu-mo">
          <span className="font-medium">Không làm được:</span> {viSaoChan}
        </p>
      )}
    </div>
  );
}

export interface KhoiGoThanhVienProps {
  thanhVien: TeamMember;
  /** Người đang bấm có phải chính người này. Tự khoá/lưu trữ/xoá mình đều bị chặn. */
  laChinhMinh: boolean;
  /** Đóng hộp thoại sửa thành viên sau khi trạng thái đã đổi. */
  onXong: () => void;
}

/**
 * "Vùng nguy hiểm" ở cuối hộp thoại sửa thành viên — ba thao tác gỡ người, xếp
 * theo mức nặng dần, mỗi thao tác một dòng:
 *
 * 1. **Khoá tài khoản** — tạm, mở lại bằng một nút. Dùng khi nghỉ phép dài,
 *    nghi ngờ tài khoản bị lộ.
 * 2. **Lưu trữ** — nhân sự nghỉ việc: mất quyền vĩnh viễn, gỡ phân công kênh để
 *    người khác nhận, nhưng **giữ toàn bộ dữ liệu lịch sử** và tên người đó ở
 *    mọi nơi đã làm.
 * 3. **Xoá tài khoản** — chỉ cho tài khoản thêm nhầm, chưa làm gì trong hệ
 *    thống (đăng nhập hay đổi mật khẩu của chính họ **không** tính). Không đủ
 *    điều kiện thì nút vẫn hiện nhưng bị vô hiệu kèm lời giải thích (máy chủ trả
 *    số liệu), để quản trị hiểu vì sao và chuyển sang Lưu trữ.
 *
 * Mọi ràng buộc đều do máy chủ kiểm; vô hiệu nút ở đây chỉ là lớp thứ hai.
 */
export function KhoiGoThanhVien({ thanhVien, laChinhMinh, onXong }: KhoiGoThanhVienProps) {
  const doiKhoa = useDoiKhoaThanhVien();
  const luuTru = useLuuTruThanhVien();
  const xoa = useXoaThanhVien();

  const [hop, setHop] = useState<Hop>(null);
  const [lyDo, setLyDo] = useState('');

  const daKhoa = thanhVien.status === 'disabled';
  /*
   * Hỏi điều kiện xoá ngay khi mở hộp thoại, không chờ bấm: nút "Xoá tài khoản"
   * phải biết mình có bị vô hiệu hay không **trước** khi người dùng bấm, và câu
   * giải thích ("đã có 3 thẻ sản xuất…") hiện luôn dưới dòng. Bỏ qua khi đang
   * sửa chính mình — lúc đó nút đã bị chặn vì lý do khác.
   */
  const kha = useKhaNangXoa(thanhVien.id, !laChinhMinh);

  const chanTuMinh = laChinhMinh
    ? 'Không thể khoá, lưu trữ hay xoá tài khoản của chính mình.'
    : undefined;
  const chanXoa = laChinhMinh
    ? chanTuMinh
    : kha.isPending
      ? 'Đang kiểm tra người này đã có dữ liệu nghiệp vụ gì…'
      : (kha.data?.lyDo ?? kha.error?.message ?? undefined);

  function dong() {
    setHop(null);
  }

  return (
    <>
      <section className="mt-6 rounded-[10px] border border-bad-chu/25 bg-bad-nen/40 p-4">
        <h3 className="text-phu font-semibold tracking-wide text-bad-chu uppercase">
          Vùng nguy hiểm
        </h3>
        <p className="mt-1 mb-2 text-phu text-chu-phu">
          Ba việc dưới đây đều cắt quyền truy cập của người này. Mỗi việc có hộp xác nhận riêng.
        </p>

        <DongNguyHiem
          ten={daKhoa ? 'Mở khoá tài khoản' : 'Khoá tài khoản'}
          moTa={
            daKhoa
              ? `Đang khoá${thanhVien.lockedAt ? ` từ ${ngayGio(thanhVien.lockedAt)}` : ''}${
                  thanhVien.lockedByName ? ` bởi ${thanhVien.lockedByName}` : ''
                }. Mở khoá là đăng nhập lại được bằng mật khẩu cũ.`
              : 'Chặn đăng nhập tạm thời, giữ nguyên vai trò và kênh được giao. Mở lại bằng một nút.'
          }
          {...(!daKhoa && chanTuMinh ? { viSaoChan: chanTuMinh } : {})}
          nut={
            <Nut
              kieu={daKhoa ? 'phu' : 'nguy-hiem'}
              disabled={!daKhoa && laChinhMinh}
              {...(!daKhoa && chanTuMinh ? { title: chanTuMinh } : {})}
              onClick={() => {
                setHop('khoa');
              }}
            >
              {daKhoa ? 'Mở khoá' : 'Khoá tài khoản'}
            </Nut>
          }
        />

        <DongNguyHiem
          ten="Lưu trữ (nghỉ việc)"
          moTa="Mất quyền vĩnh viễn và gỡ phân công kênh, nhưng giữ toàn bộ lịch sử và tên người này ở mọi nơi."
          {...(chanTuMinh ? { viSaoChan: chanTuMinh } : {})}
          nut={
            <Nut
              kieu="phu"
              disabled={laChinhMinh}
              {...(chanTuMinh ? { title: chanTuMinh } : {})}
              onClick={() => {
                setLyDo('');
                setHop('luu-tru');
              }}
            >
              Lưu trữ
            </Nut>
          }
        />

        <DongNguyHiem
          ten="Xoá tài khoản"
          moTa="Chỉ cho tài khoản thêm nhầm, chưa làm gì trong hệ thống. Không lùi lại được."
          {...(chanXoa ? { viSaoChan: chanXoa } : {})}
          nut={
            <Nut
              kieu="nguy-hiem"
              disabled={Boolean(chanXoa)}
              {...(chanXoa ? { title: chanXoa } : {})}
              onClick={() => {
                setHop('xoa');
              }}
            >
              Xoá tài khoản
            </Nut>
          }
        />
      </section>

      {/* ---------------------------- Khoá / mở khoá --------------------------- */}

      {hop === 'khoa' && (
        <HopXacNhan
          title={daKhoa ? 'Mở khoá tài khoản?' : 'Khoá tài khoản này?'}
          nhanXacNhan={daKhoa ? 'Mở khoá' : 'Khoá tài khoản'}
          nguyHiem={!daKhoa}
          dangChay={doiKhoa.isPending}
          loi={doiKhoa.error?.message ?? null}
          onXacNhan={() => {
            doiKhoa.mutate(
              { id: thanhVien.id, khoa: !daKhoa },
              {
                onSuccess: () => {
                  dong();
                  onXong();
                },
              },
            );
          }}
          onDong={dong}
        >
          {daKhoa ? (
            <>
              <p>
                <strong className="text-chu">{thanhVien.name}</strong> ({thanhVien.email}) sẽ đăng
                nhập lại được ngay, bằng <strong className="text-chu">mật khẩu cũ</strong>.
              </p>
              <p>Vai trò và kênh được giao vẫn như trước khi khoá.</p>
            </>
          ) : (
            <>
              <p>
                <strong className="text-chu">{thanhVien.name}</strong> ({thanhVien.email}) sẽ:
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>không đăng nhập được nữa;</li>
                <li>bị đăng xuất khỏi mọi thiết bị đang mở, ngay ở lần tải trang kế tiếp;</li>
                <li>không hiện trong ô chọn người phụ trách khi giao việc mới.</li>
              </ul>
              <p>
                <strong className="text-chu">Không mất gì:</strong> tài khoản không bị xoá, lịch sử
                và nhật ký thao tác giữ nguyên, kênh được giao cũng giữ nguyên. Mở khoá lại được bất
                cứ lúc nào.
              </p>
            </>
          )}
        </HopXacNhan>
      )}

      {/* ------------------------------- Lưu trữ ------------------------------ */}

      {hop === 'luu-tru' && (
        <HopXacNhan
          title="Lưu trữ nhân sự đã nghỉ?"
          nhanXacNhan="Lưu trữ tài khoản"
          nguyHiem
          rong="lg"
          chan={lyDo.trim().length === 0}
          dangChay={luuTru.isPending}
          loi={luuTru.error?.message ?? null}
          onXacNhan={() => {
            luuTru.mutate(
              { id: thanhVien.id, reason: lyDo.trim() },
              {
                onSuccess: () => {
                  dong();
                  onXong();
                },
              },
            );
          }}
          onDong={dong}
        >
          <p>
            <strong className="text-chu">{thanhVien.name}</strong> ({thanhVien.email}) sẽ mất quyền
            truy cập <strong className="text-chu">vĩnh viễn</strong>:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>không đăng nhập được, mọi thiết bị đang mở bị đăng xuất ngay;</li>
            <li>không đặt lại mật khẩu hay gửi link mời được nữa;</li>
            <li>
              {thanhVien.channels.length > 0
                ? `${String(thanhVien.channels.length)} kênh đang phụ trách được gỡ ra để người khác nhận`
                : 'không còn hiện trong ô chọn người phụ trách'}
              .
            </li>
          </ul>
          <p>
            <strong className="text-chu">Giữ nguyên:</strong> toàn bộ dữ liệu người này tạo hoặc
            sửa, và tên họ ở mọi nơi — nhật ký, thẻ sản xuất, hợp đồng… kèm nhãn "đã nghỉ". Hệ thống
            chụp lại "hồ sơ khi nghỉ" (vai trò, kênh đang giữ, ngày tham gia) để tra lại về sau.
          </p>
          <p className="text-chu-mo">
            Nhân sự quay lại thì dùng Khôi phục ở tab Lưu trữ — kênh cũ không tự gán lại.
          </p>

          <div className="pt-2">
            <O
              label="Lý do lưu trữ"
              value={lyDo}
              onChange={setLyDo}
              moTa='Ví dụ: "Nghỉ việc 31/10/2026". Lý do này hiện ở tab Lưu trữ và trong nhật ký.'
            />
          </div>
        </HopXacNhan>
      )}

      {/* -------------------------------- Xoá --------------------------------- */}

      {hop === 'xoa' && (
        <HopXacNhan
          title="Xoá hẳn tài khoản này?"
          nhanXacNhan="Xoá tài khoản"
          nguyHiem
          rong="lg"
          chan={kha.isPending || kha.isError || !kha.data?.xoaDuoc}
          goLai={{ nhan: `Gõ lại email để xác nhận: ${thanhVien.email}`, giaTri: thanhVien.email }}
          dangChay={xoa.isPending}
          loi={xoa.error?.message ?? kha.error?.message ?? null}
          onXacNhan={() => {
            xoa.mutate(
              { id: thanhVien.id },
              {
                onSuccess: () => {
                  dong();
                  onXong();
                },
              },
            );
          }}
          onDong={dong}
        >
          {kha.isPending && (
            <p>Đang kiểm tra người này đã có dữ liệu nghiệp vụ gì trong hệ thống…</p>
          )}

          {kha.data && !kha.data.xoaDuoc && (
            <>
              <p role="alert" className="rounded-[10px] bg-bad-nen p-3 text-bad-chu">
                {kha.data.lyDo}
              </p>
              {kha.data.tong > 0 && (
                <p className="text-chu-mo">
                  Xoá tài khoản sẽ làm những bản ghi này mất dấu người thực hiện. Lưu trữ giữ nguyên
                  tất cả, kèm nhãn &quot;đã nghỉ&quot;.
                </p>
              )}
            </>
          )}

          {kha.data?.xoaDuoc && (
            <>
              <p>
                <strong className="text-chu">{thanhVien.name}</strong> ({thanhVien.email}) chưa làm
                gì trong hệ thống, nên xoá hẳn được. Những lần đăng nhập hay đổi mật khẩu của chính
                họ không tính — các dòng nhật ký đó vẫn được giữ lại.
              </p>
              <p>
                Sẽ mất: tài khoản, {String(kha.data.seXoaKem.sessions)} phiên đăng nhập,{' '}
                {String(kha.data.seXoaKem.roles)} vai trò,{' '}
                {String(kha.data.seXoaKem.channelAssignments)} phân công kênh và{' '}
                {String(kha.data.seXoaKem.invites)} lời mời của email này.
              </p>
              <p className="text-chu-mo">
                Không lùi lại được. Nhật ký giữ một dòng "đã xoá tài khoản" kèm email và tên để còn
                dấu vết.
              </p>
            </>
          )}

          {kha.data && kha.data.tong > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-chu-phu">
              {(Object.entries(kha.data.thamChieu) as [ThamChieuNguoiDung, number][])
                .filter(([, n]) => n > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([k, n]) => (
                  <li key={k}>
                    {String(n)} {NHAN_THAM_CHIEU[k]}
                  </li>
                ))}
            </ul>
          )}
        </HopXacNhan>
      )}
    </>
  );
}
